import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  Button,
  Divider,
  Form,
  Input,
  List,
  message,
  Radio,
  Select,
  Spin,
  Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { jsonValidator, selectFilterFunction } from "../../Constants/Utilities";
import {
  BulkDeviceFormats,
  BulkDeviceJSON,
  formItemLayout,
  tailItemLayout,
} from "../../Constants/Constants";
import { apiCallback } from "../../ApiHandlers/Common";
import { onFormSubmit } from "../../ApiHandlers/DeviceApiHandler";
import { convertDeviceModelListToDropdown } from "../../Helpers/DeviceModelHelpers";
import { BreadCrumbs } from "../Common/BreadCrumbs";

const BulkAddDevice = (props) => {
  console.log("BulkAddDevices props", props);
  const [form] = Form.useForm();
  const [processing, setProcessing] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [modelData, setModelData] = useState([]);
  const [fetchingModelData, setFetchingModelData] = useState(false);

  useEffect(() => {
    !props.deviceModelRecord && fetchDeviceModelData();
  }, []);

  const onFormatChange = () => {
    setError(null);
    setFile(null);
  };

  const onFormReset = () => {
    form.resetFields();
    setError(null);
    setFile(null);
  };

  const fetchDeviceModelData = () => {
    const errorCallback = (error) => {
      setFetchingModelData(false);
      message.error("Error fetching Device Model list");
    };
    const successCallback = (data) => {
      const deviceModelData = convertDeviceModelListToDropdown(data);
      console.log("fetchDeviceModelData data", deviceModelData);
      setModelData(deviceModelData);
      setFetchingModelData(false);
    };
    try {
      console.log("fetchDeviceModelData...");
      const { history, deviceModelApi } = props;
      setFetchingModelData(true);
      deviceModelApi.getDeviceModels(
        apiCallback({ history, errorCallback, successCallback })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(
            props.orders ? "Add Devices in Bulk" : "Bulk Import Orders"
          )}
          onParentActionClick={props.onActionClick(null, null)}
        />
      </div>
      <Divider className="divider" />
      <Form
        form={form}
        size={"middle"}
        layout="horizontal"
        name="bulk-add-device-form"
        requiredMark={"optional"}
        onFinish={onFormSubmit({ props, file, setError, setProcessing })}
        initialValues={{
          deviceModel: props.deviceModelRecord?.modelNumber,
          format: BulkDeviceFormats.csv.value,
          devices: BulkDeviceJSON,
          file: [],
        }}
        {...formItemLayout}
      >
        <Spin spinning={processing} tip={"Processing, please wait..."}>
          <Form.Item
            name="format"
            label="Data Format"
            rules={[{ required: true }]}
          >
            <Radio.Group onChange={onFormatChange}>
              {Object.values(BulkDeviceFormats).map((f) => (
                <Radio key={f.value} value={f.value}>
                  {f.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(preVals, curVals) =>
              preVals.format !== curVals.format
            }
          >
            {({ getFieldValue }) =>
              getFieldValue("format") === BulkDeviceFormats.json.value ? (
                <Form.Item
                  name="devices"
                  label="Device Data"
                  validateTrigger={["onBlur"]}
                  rules={[
                    {
                      required: true,
                      validator: jsonValidator({ type: "array" }),
                    },
                  ]}
                >
                  <Input.TextArea
                    title="Device Data must be JSON object array"
                    autoSize={{ minRows: 10, maxRows: 20 }}
                    style={{ width: "70%", maxWidth: "600px" }}
                  />
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    name="deviceModel"
                    label="Device Model:"
                    rules={[
                      {
                        required: true,
                        message: "Please select Device Model(s)",
                      },
                    ]}
                  >
                    {props.deviceModelRecord ? (
                      <Input
                        disabled
                        style={{ width: "90%", maxWidth: "300px" }}
                      />
                    ) : (
                      <Select
                        showSearch
                        allowClear
                        style={{ width: "90%", maxWidth: "300px" }}
                        loading={fetchingModelData}
                        disabled={fetchingModelData}
                        filterOption={selectFilterFunction}
                        placeholder={
                          fetchingModelData
                            ? "Loading Device Model list..."
                            : "Select Device Model"
                        }
                      >
                        {modelData.map((item) => (
                          <Select.Option
                            key={item.modelNumber}
                            title={item.modelNumber}
                          >
                            {item.modelNumber}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                  <Form.Item
                    name="file"
                    valuePropName="file"
                    className="file-upload"
                    getValueFromEvent={(e) => {
                      return e && e.fileList;
                    }}
                    rules={[
                      {
                        required: true,
                        message: "Please select device data file",
                      },
                    ]}
                  >
                    <Upload.Dragger
                      maxCount={1}
                      onRemove={onFormatChange}
                      onChange={() => setError(null)}
                      customRequest={({ file, onSuccess }) => {
                        setFile(file);
                        setTimeout(() => {
                          onSuccess("ok");
                        }, 0);
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                </>
              )
            }
          </Form.Item>

          {error && error.length && (
            <div className="error-list">
              <List
                bordered
                size="small"
                dataSource={error}
                header={
                  <b>
                    {error.length} Error{error.length > 1 ? "s" : ""}:
                  </b>
                }
                className="simple-list custom-scrollbar"
                renderItem={(item) => <List.Item>{item}</List.Item>}
              />
            </div>
          )}

          <Form.Item {...tailItemLayout}>
            <Button type="primary" htmlType="submit" className="submit-button">
              Add
            </Button>
            <Button
              htmlType="button"
              onClick={onFormReset}
              className="cancel-button"
            >
              Cancel
            </Button>
          </Form.Item>
        </Spin>
      </Form>
    </div>
  );
};

BulkAddDevice.propTypes = {
  onSubmit: PropTypes.func,
  onActionClick: PropTypes.func,

  deviceModelApi: PropTypes.object,
  deviceApi: PropTypes.object,
  orderApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  deviceModelApi: state.apis.deviceModelApi,
  deviceApi: state.apis.deviceApi,
  orderApi: state.apis.orderApi,
});
const connectedBulkAddDevice = connect(mapStateToProps)(BulkAddDevice);
export default withRouter(connectedBulkAddDevice);
